// Kernel vocabulary for the GFM cell-align attribute (task 5.1): hoisted from
// the app table-menu file, which now re-exports these from the door.
export type CellAlign = 'left' | 'center' | 'right';

const CELL_ALIGN_VALUES: readonly CellAlign[] = ['left', 'center', 'right'];

export const isCellAlign = (value: unknown): value is CellAlign =>
  CELL_ALIGN_VALUES.includes(value as CellAlign);
import type {
  JSONContent,
  MarkdownParseHelpers,
  MarkdownRendererHelpers,
  MarkdownToken,
  Node,
} from '@tiptap/core';
import { Tokenizer, type TokenizerObject, type Tokens } from 'marked';
import { retainEscapesAsText } from './paragraph-markdown';

// GFM separator markers per column alignment. `null` (no alignment) stays the
// bare `---`, matching the compact serializer's default.
const ALIGN_MARKER: Record<CellAlign, string> = {
  left: ':---',
  center: ':---:',
  right: '---:',
};

type TableCellEntry = { text: string; align: CellAlign | null };

type TableMarkdownToken = MarkdownToken & {
  header?: { tokens?: MarkdownToken[] }[];
  rows?: { tokens?: MarkdownToken[] }[][];
  align?: (CellAlign | null)[];
};

// GFM cells are single-line: collapse internal whitespace runs to one space.
const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

// Escape literal pipes so cell text never splits a column on re-parse.
const escapeCell = (value: string): string => value.replace(/\|/g, '\\|');

// An empty cell holds an empty paragraph, which the paragraph serializer would
// render as the `&nbsp;` blank-line marker — wrong inside a single-line cell.
// Detect emptiness from the node so a cell whose text is literally `&nbsp;`
// isn't stripped.
const isEmptyCell = (cellNode: JSONContent): boolean =>
  (cellNode.content ?? []).every((child) => !child.content?.length);

const renderRow = (cells: string[]): string => `| ${cells.join(' | ')} |`;

/**
 * Adds a GFM column-alignment attribute to a table cell node. `@tiptap/extension-table`
 * carries no alignment in its schema, so `:---` markers are otherwise lost on parse.
 * The attribute renders as `text-align` (so alignment shows in the editor) and feeds
 * the compact serializer's separator row.
 */
export const withCellAlign = (cell: Node): Node =>
  cell.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        align: {
          default: null,
          parseHTML: (element: HTMLElement) => {
            const value = element.style.textAlign || element.getAttribute('align');
            return isCellAlign(value) ? value : null;
          },
          renderHTML: (attributes: { align?: CellAlign | null }) =>
            attributes.align ? { style: `text-align: ${attributes.align}` } : {},
        },
      };
    },
  });

// Headerless-table convention, shared with the vMarkdown note pipeline: a run
// of consecutive pipe rows carrying no `|---|` delimiter line is a table whose
// header row was deleted, not paragraph text. GFM requires the delimiter, so
// marked lexes such a run as literal pipe text and the two editors would
// disagree about stored content.
//
// Only the convention is declared here; every grammar decision stays marked's.
// The run's extent is marked's own paragraph rule — which already stops before
// a row that opens a real delimiter table, so an intact table adjacent to the
// run is never swallowed — and the cells come from marked's table tokenizer,
// handed a synthesised delimiter line.
const PIPE_ROW_START = /^ {0,3}\|/;
const PIPE_ROW_LINE = /^ {0,3}\|.*\|[ \t]*$/;
// The one GFM shape written out locally rather than delegated: marked exposes
// no predicate for "this line is a delimiter row" without a header line above
// it to pair with, and the convention has to exclude a run that opens with one.
// Cells carry marked's own delimiter grammar (`:?-+:?`), so a row of empty
// cells (`| | |`) stays a table row rather than becoming a separator.
const DELIMITER_ROW_LINE = /^ {0,3}\|(?: *:?-+:? *\|)+[ \t]*$/;
// Pipe-wrapped prose (`| just text |`) is one cell, and keeps rendering as a
// paragraph — a table needs at least two columns.
const MIN_HEADERLESS_COLUMNS = 2;

const delimiterOfWidth = (columns: number): string => `|${' --- |'.repeat(columns)}`;

/**
 * True when marked splits `line` into exactly as many cells as `delimiter` has.
 * marked's own header/delimiter cell-count match is the adjudicator: GFM's
 * split honours escaped pipes, so counting them here would re-implement it.
 */
const rowMatchesWidth = (
  tokenizer: Tokenizer,
  line: string,
  delimiter: string
): boolean =>
  Tokenizer.prototype.table.call(tokenizer, `${line}\n${delimiter}`) !== undefined;

export const headerlessTableTokenizer: TokenizerObject = {
  table(src: string): Tokens.Table | undefined {
    const delimited = Tokenizer.prototype.table.call(this, src);
    if (delimited || !PIPE_ROW_START.test(src)) return delimited;

    const paragraph = this.rules.block.paragraph.exec(src);
    if (!paragraph?.[1]) return undefined;
    // Mirrors marked's own paragraph tokenizer, which drops the captured
    // trailing newline before treating the capture as content.
    const runText = paragraph[1].replace(/\n$/, '');

    const [headerLine = '', ...bodyLines] = runText.split('\n');
    const isHeaderlessRun = [headerLine, ...bodyLines].every(
      (line) => PIPE_ROW_LINE.test(line) && !DELIMITER_ROW_LINE.test(line)
    );
    if (!isHeaderlessRun) return undefined;

    // Settle the width against the first row alone — a fenced row has at most
    // one cell fewer than it has pipes, a safe search bound and never a count.
    const maxColumns = (headerLine.match(/\|/g) ?? []).length;
    let delimiter: string | undefined;
    for (let columns = MIN_HEADERLESS_COLUMNS; columns <= maxColumns; columns++) {
      const candidate = delimiterOfWidth(columns);
      if (rowMatchesWidth(this, headerLine, candidate)) {
        delimiter = candidate;
        break;
      }
    }
    if (!delimiter) return undefined;

    // Every row has to be exactly this wide. marked pads a narrower row and
    // TRUNCATES a wider one, so a ragged run would drop a cell while `raw`
    // swallowed the line it came from — silent loss of clinical content, and
    // worse than the literal pipe text this replaces. Refuse the whole run
    // instead, mirroring vMarkdown, which also converts only when every row
    // qualifies.
    if (!bodyLines.every((line) => rowMatchesWidth(this, line, delimiter)))
      return undefined;

    const body = bodyLines.length > 0 ? `\n${bodyLines.join('\n')}` : '';
    const table = Tokenizer.prototype.table.call(
      this,
      `${headerLine}\n${delimiter}${body}`
    );
    if (!table || table.rows.length !== bodyLines.length) return undefined;

    // Row 0 was the header only to satisfy GFM's grammar — hand it back as a
    // body row. `raw` is marked's paragraph extent, never a local scan.
    return {
      ...table,
      raw: paragraph[0],
      header: [],
      rows: [table.header.map((cell) => ({ ...cell, header: false })), ...table.rows],
    };
  },
};

// Cells lex their own inline content, so they need the escape inverse too.
// Without it every marker the paragraph renderer escapes is dropped on reload:
// a lone `\-` (a common "nil" value) comes back blank, and `\- no findings` or
// `1\. part water` come back stripped of the marker.
const parseCell = (
  type: 'tableHeader' | 'tableCell',
  cell: { tokens?: MarkdownToken[] },
  align: CellAlign | null,
  helpers: MarkdownParseHelpers
): JSONContent =>
  helpers.createNode(type, align ? { align } : {}, [
    {
      type: 'paragraph',
      content: helpers.parseInline(retainEscapesAsText(cell.tokens ?? [])),
    },
  ]);

/**
 * Mirrors the upstream table parse but threads marked's per-column `token.align`
 * onto each cell. Grammar stays delegated to marked — only the alignment metadata
 * is read, never re-parsed.
 */
export const parseTableMarkdown = (
  token: MarkdownToken,
  helpers: MarkdownParseHelpers
): JSONContent => {
  const { header = [], rows = [], align = [] } = token as TableMarkdownToken;
  const tableRows: JSONContent[] = [];

  if (header.length > 0) {
    tableRows.push(
      helpers.createNode(
        'tableRow',
        {},
        header.map((cell, index) =>
          parseCell('tableHeader', cell, align[index] ?? null, helpers)
        )
      )
    );
  }

  for (const row of rows) {
    tableRows.push(
      helpers.createNode(
        'tableRow',
        {},
        row.map((cell, index) =>
          parseCell('tableCell', cell, align[index] ?? null, helpers)
        )
      )
    );
  }

  return helpers.createNode('table', undefined, tableRows);
};

/**
 * Compact GFM table serializer. The built-in `@tiptap/extension-table` renderer
 * pads columns to their widest cell and prepends a blank line, rewriting clean
 * tables on save. Emits the minimal form (single-space cells, `---` separators,
 * no surrounding blank line) so a compact table round-trips byte-identical.
 * Column alignment is preserved via `:---` / `:---:` / `---:` separators.
 * Inlines come from `helpers.renderChildren`, never a hand-rolled pass.
 */
export const renderTableMarkdown = (
  node: JSONContent,
  helpers: MarkdownRendererHelpers
): string => {
  const rows: TableCellEntry[][] = (node.content ?? []).map((rowNode) =>
    (rowNode.content ?? []).map((cellNode) => ({
      text: isEmptyCell(cellNode)
        ? ''
        : escapeCell(collapseWhitespace(helpers.renderChildren(cellNode.content ?? []))),
      align: isCellAlign(cellNode.attrs?.align) ? cellNode.attrs.align : null,
    }))
  );

  const columnCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  if (columnCount === 0) return '';

  const cellAt = (row: TableCellEntry[], index: number): string => row[index]?.text ?? '';
  const fillColumns = (resolve: (index: number) => string): string =>
    renderRow(Array.from({ length: columnCount }, (_, index) => resolve(index)));

  // GFM alignment is per column; read it from the first row that defines it
  // (header normally, body otherwise).
  const separatorAt = (index: number): string => {
    for (const row of rows) {
      const align = row[index]?.align;
      if (align) return ALIGN_MARKER[align];
    }
    return '---';
  };

  const renderedRows = rows.map((row) => fillColumns((index) => cellAt(row, index)));

  // No header cell anywhere means the headerless convention above: emit bare
  // pipe rows rather than a header the table never had. The check spans the
  // whole table, never one row — GFM's header line is positional, so keying off
  // row 0's cell type would fabricate a blank header for a body row inserted
  // above the real one, and duplicate row 0 into the body.
  const hasHeaderCell = (node.content ?? []).some((rowNode) =>
    (rowNode.content ?? []).some((cellNode) => cellNode.type === 'tableHeader')
  );
  // Only at the widths the tokenizer will read back. A single-column run is
  // pipe-wrapped prose to it, so emitting one would round-trip the table into
  // paragraphs; keep the delimited form there and let row 0 become the header.
  if (!hasHeaderCell && columnCount >= MIN_HEADERLESS_COLUMNS)
    return renderedRows.join('\n');

  const [headerRow = '', ...bodyRows] = renderedRows;
  return [headerRow, fillColumns(separatorAt), ...bodyRows].join('\n');
};

export const tableMarkdownSpec = {
  parseMarkdown: parseTableMarkdown,
  renderMarkdown: renderTableMarkdown,
};
